import React, { useState } from 'react';
import {
  Box, Typography, Grid, TextField, Button, FormControl, InputLabel,
  Select, MenuItem, Chip, Paper, Stack, Card, CardContent, CircularProgress, Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr } from 'date-fns/locale';
import { Add, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks';
import { UPDATE_JOB_PROJECT_DATES } from '../../graphql/mutations/jobs';
import graphqlClient from '../../utils/graphqlClient';
import { formatDateForHasura } from '../../utils/dateUtils';

const ProjectDatesSection = ({ job, onSuccess, onError, onRefresh, styles }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddExtension, setShowAddExtension] = useState(false);
  const [extensionForm, setExtensionForm] = useState({ type: '', startDate: '', endDate: '', reason: '' });

  const projectExtensions = Array.isArray(job?.project_extensions) ? job.project_extensions : [];
  const hasOtelemeAlready = projectExtensions.some(ext => ext.type === 'OTELME');
  const canDeleteExtension = user?.role === 'ROOT' || user?.role === 'TEAM_MANAGER';

  const handleAddExtension = async () => {
    if (!extensionForm.type || !extensionForm.startDate || !extensionForm.reason?.trim()) {
      setError(t('jobStatusUpdate.projectDates.validationError') || 'Tür, başlangıç tarihi ve sebep zorunludur');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const createdByName = user ? `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim() || '-' : '-';
      const newEntry = {
        type: extensionForm.type,
        startDate: extensionForm.startDate,
        endDate: extensionForm.endDate || null,
        reason: extensionForm.reason.trim(),
        createdAt: new Date().toISOString(),
        createdBy: user?.id ?? null,
        createdByName,
      };
      await graphqlClient.mutate(UPDATE_JOB_PROJECT_DATES, {
        id: job.id,
        set: { project_extensions: [...projectExtensions, newEntry] },
      });
      onSuccess?.('Proje tarihi eklendi');
      onRefresh?.();
      setExtensionForm({ type: '', startDate: '', endDate: '', reason: '' });
      setShowAddExtension(false);
    } catch (err) {
      const msg = err.message || 'Hata oluştu';
      setError(msg);
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExtension = async (deleteIndex) => {
    if (!canDeleteExtension || loading) return;
    setLoading(true);
    setError(null);
    try {
      await graphqlClient.mutate(UPDATE_JOB_PROJECT_DATES, {
        id: job.id,
        set: { project_extensions: projectExtensions.filter((_, i) => i !== deleteIndex) },
      });
      onSuccess?.('Proje tarihi silindi');
      onRefresh?.();
    } catch (err) {
      const msg = err.message || 'Hata oluştu';
      setError(msg);
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Divider sx={styles?.divider} />
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" color="text.primary" sx={styles?.sectionTitle}>
            {t('jobStatusUpdate.projectDates.title')}
          </Typography>
          <Button variant="outlined" startIcon={<Add />} onClick={() => setShowAddExtension(prev => !prev)} disabled={loading}>
            {showAddExtension ? t('jobStatusUpdate.projectDates.close') : t('jobStatusUpdate.projectDates.addButton')}
          </Button>
        </Stack>

        {projectExtensions.length === 0 && !showAddExtension && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, px: 1 }}>
            {t('jobStatusUpdate.projectDates.empty')}
          </Typography>
        )}

        {projectExtensions.length > 0 && (
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            {projectExtensions.map((item, index) => (
              <Card key={index} variant="outlined" sx={{ backgroundColor: '#fafafa' }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack spacing={1}>
                    <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1.5}>
                      <Chip size="small"
                        label={item.type === 'UZATMA' ? t('jobStatusUpdate.projectDates.uzatma') : item.type === 'OTELME' ? t('jobStatusUpdate.projectDates.oteleme') : t('jobStatusUpdate.projectDates.diger')}
                        color={item.type === 'UZATMA' ? 'primary' : item.type === 'OTELME' ? 'warning' : 'default'}
                        variant="outlined" />
                      {item.startDate && (
                        <Typography variant="body2" color="text.secondary">
                          {t('jobStatusUpdate.projectDates.startDate')}: {new Date(item.startDate).toLocaleDateString('tr-TR')}
                        </Typography>
                      )}
                      {item.endDate && (
                        <Typography variant="body2" color="text.secondary">
                          {t('jobStatusUpdate.projectDates.endDate')}: {new Date(item.endDate).toLocaleDateString('tr-TR')}
                        </Typography>
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }} />
                      {canDeleteExtension && (
                        <Button variant="text" color="error" size="small" startIcon={<Delete />}
                          onClick={() => handleDeleteExtension(index)} disabled={loading}>
                          {t('jobStatusUpdate.projectDates.delete')}
                        </Button>
                      )}
                      {(item.createdAt || item.createdByName) && (
                        <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ color: 'text.secondary' }}>
                          {item.createdByName && (
                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                              {t('jobStatusUpdate.projectDates.createdBy')}: {item.createdByName}
                            </Typography>
                          )}
                          {item.createdAt && (
                            <Typography variant="caption" sx={{ px: 1, py: 0.25, borderRadius: 1, backgroundColor: 'action.hover', fontVariantNumeric: 'tabular-nums' }}>
                              {new Date(item.createdAt).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                              {' · '}
                              {new Date(item.createdAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          )}
                        </Stack>
                      )}
                    </Stack>
                    {item.reason && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', pt: 1.25, borderTop: 1, borderColor: 'divider' }}>
                        {t('jobStatusUpdate.projectDates.reason')}: {item.reason}
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}

        {showAddExtension && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle1">{t('jobStatusUpdate.projectDates.addTitle')}</Typography>
              <Button variant="contained" onClick={handleAddExtension} disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Add />}>
                {loading ? t('jobStatusUpdate.projectDates.saving') : t('jobStatusUpdate.projectDates.save')}
              </Button>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth required sx={{ minWidth: 200 }}>
                  <InputLabel>{t('jobStatusUpdate.projectDates.type')}</InputLabel>
                  <Select value={extensionForm.type} label={t('jobStatusUpdate.projectDates.type')}
                    onChange={e => setExtensionForm(p => ({ ...p, type: e.target.value }))}>
                    <MenuItem value="UZATMA">{t('jobStatusUpdate.projectDates.uzatma')}</MenuItem>
                    {!hasOtelemeAlready && <MenuItem value="OTELME">{t('jobStatusUpdate.projectDates.oteleme')}</MenuItem>}
                    <MenuItem value="DIGER">{t('jobStatusUpdate.projectDates.diger')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
                  <DatePicker label={t('jobStatusUpdate.projectDates.startDate')} format="dd.MM.yyyy"
                    views={['year', 'month', 'day']}
                    slotProps={{ textField: { fullWidth: true } }}
                    value={extensionForm.startDate ? new Date(extensionForm.startDate) : null}
                    onChange={v => setExtensionForm(p => ({ ...p, startDate: formatDateForHasura(v) || '' }))} />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={4}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
                  <DatePicker label={t('jobStatusUpdate.projectDates.endDate')} format="dd.MM.yyyy"
                    views={['year', 'month', 'day']}
                    slotProps={{ textField: { fullWidth: true } }}
                    value={extensionForm.endDate ? new Date(extensionForm.endDate) : null}
                    onChange={v => setExtensionForm(p => ({ ...p, endDate: formatDateForHasura(v) || '' }))} />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12}>
                <TextField label={t('jobStatusUpdate.projectDates.reason')} value={extensionForm.reason} fullWidth required multiline minRows={2}
                  onChange={e => setExtensionForm(p => ({ ...p, reason: e.target.value }))} />
              </Grid>
            </Grid>
          </Paper>
        )}

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>{error}</Typography>
        )}
      </Box>
    </>
  );
};

export default ProjectDatesSection;
